import {Plugins, withMeta, withSystem} from '@packages/systems/wfdl';
import {
  TEnum,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {FILE_DEFAULT_LABEL, FILE_DEFAULT_LABEL_CAP} from '../constants';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {UserFormRequired} from '../UserFormRequired';
import React, {useCallback} from 'react';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {usysFieldType, USYS_FIELD_PATH} from '@packages/systems/users/usysWfdl';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

const fields = {
  users: usysFieldType,
  temp: TRecord({
    state: withSystem(
      {
        defaultValue: 'default',
        label: 'State',
        isFullWidth: true,
        isLocalizable: true,
      },
      TEnum({
        default: 'Default',
        uploading: 'Uploading',
        success: 'Success',
        error: 'Error',
      })
    ),
  }),
  form: TRecord({
    type: withSystem({defaultValue: 'file-upload-input', private: true}, TText),
    name: withMeta(
      {placeholder: 'File upload'},
      withSystem(
        {
          label: 'Name',
          defaultValue: FILE_DEFAULT_LABEL_CAP,
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      )
    ),
    manageTypes: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Allow only specific file types',
        private: true,
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    acceptDocuments: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Document .doc, .pdf, .txt…',
        private: true,
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    acceptImages: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Image .jpg, .gif, .png…',
        private: true,
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    acceptCustom: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Custom',
        private: true,
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    customTypes: withMeta(
      {placeholder: 'Format: .psd, .pdf, .doc, .png'},
      withSystem(
        {isLocalizable: true},
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      )
    ),
  }),
  attr: TRecord({
    id: withSystem(
      {private: true, defaultValue: FILE_DEFAULT_LABEL, isLocalizable: true},
      TText
    ),
    required: withSystem(
      {labelVariant: 'hidden', label: 'Required', isLocalizable: true},
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    multiple: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Multiple',
        private: true,
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    accept: withSystem({private: true, defaultValue: ''}, TText),
    type: withSystem({defaultValue: FILE_DEFAULT_LABEL, private: true}, TText),
    name: withSystem(
      {defaultValue: FILE_DEFAULT_LABEL, private: true, isLocalizable: true},
      TText
    ),
    'data-name': withSystem(
      {
        defaultValue: FILE_DEFAULT_LABEL_CAP,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
    'aria-hidden': withSystem({defaultValue: 'true', private: true}, TText),
    tabIndex: withSystem({defaultValue: '-1', private: true}, TText),
  }),
} as const;

const getExpressionFromPath = (
  expr: ContextualExpression,
  path: Array<{
    at: string;
    in: 'Record';
  }>
) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};
const getExpressionText = (
  expr: ContextualExpression,
  path: Array<{
    at: string;
    in: 'Record';
  }>
) => {
  return (
    ContextualExpressions.getText(
      stripAnn(getExpressionFromPath(expr, path))
    ) ?? ''
  );
};
const UserFormFileUploadInputType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function UserFileUploadInputRender({expr, dispatch}) {
    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    const stateExpr = getExpressionFromPath(expr, TEMP_STATE_PATH);
    const stateDispatch = useCallback(dispatchAt(TEMP_STATE_PATH), [
      dispatchAt,
    ]);

    // @ts-expect-error - TS2345 - Argument of type 'readonly [{ readonly in: "Record"; readonly at: "users"; }, { readonly in: "Record"; readonly at: "field"; }]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
    const userFieldId = getExpressionText(expr, USYS_FIELD_PATH);

    return (
      <Grid>
        {/* Default / uploading / success / error switch */}
        <ExpressionEditor expression={stateExpr} dispatch={stateDispatch} />
        {/* Required */}
        <UserFormRequired fieldId={userFieldId} />
      </Grid>
    );
  },
});

export default UserFormFileUploadInputType;
